import React from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

interface SeasonalAverages {
  winter: number;
  spring: number;
  summer: number;
  fall: number;
}

interface PatternAnalysisProps {
  hourlyPattern: number[];
  hourlyStdDev?: number[];
  weekdayAverage: number;
  weekendAverage: number;
  seasonalAverages: SeasonalAverages;
  unit: string;
  color: string;
  title: string;
}

export const PatternAnalysis: React.FC<PatternAnalysisProps> = ({
  hourlyPattern,
  hourlyStdDev,
  weekdayAverage,
  weekendAverage,
  seasonalAverages,
  unit,
  color,
  title
}) => {
  const { t } = useTranslation();
  // Calculate upper and lower bounds if stdDev is provided
  const upperBound = hourlyStdDev 
    ? hourlyPattern.map((val, idx) => val + (hourlyStdDev[idx] || 0))
    : null;
  
  const lowerBound = hourlyStdDev
    ? hourlyPattern.map((val, idx) => Math.max(0, val - (hourlyStdDev[idx] || 0)))
    : null;

  const datasets = [
    {
      label: title,
      data: hourlyPattern,
      borderColor: color,
      backgroundColor: `${color}20`,
      tension: 0.4,
      fill: false,
      order: 1,
    },
  ];

  // Add standard deviation bands if available
  if (upperBound && lowerBound) {
    datasets.unshift({
      label: t('dataAlignment.patterns.upperBound'),
      data: upperBound,
      borderColor: `${color}40`,
      backgroundColor: `${color}10`,
      borderWidth: 1,
      borderDash: [5, 5],
      tension: 0.4,
      fill: '+1',
      order: 2,
    } as any);

    datasets.unshift({
      label: t('dataAlignment.patterns.lowerBound'),
      data: lowerBound,
      borderColor: `${color}40`,
      backgroundColor: `${color}10`,
      borderWidth: 1,
      borderDash: [5, 5],
      tension: 0.4,
      fill: false,
      order: 3,
    } as any);
  }

  const chartData = {
    labels: Array.from({ length: 24 }, (_, i) => i.toString()),
    datasets,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            if (context.dataset.label === t('dataAlignment.patterns.upperBound') ||
                context.dataset.label === t('dataAlignment.patterns.lowerBound')) {
              return null; // Hide tooltip for bounds
            }
            return `${context.parsed.y.toFixed(2)} ${unit}`;
          },
        },
        filter: (tooltipItem: any) => {
          // Only show tooltip for main line
          return tooltipItem.datasetIndex === datasets.length - 1;
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: t('dataAlignment.patterns.hourOfDay'),
        },
      },
      y: {
        title: {
          display: true,
          text: unit,
        },
      },
    },
  };

  return (
    <>
      {/* Hourly Pattern Chart */}
      <div className="mb-6">
        <h5 className="text-sm font-medium text-swiss-charcoal mb-3">{t('dataAlignment.patterns.dailyPattern')}</h5>
        <div className="h-48">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Weekday/Weekend Variation */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-swiss-pearl p-3 rounded-lg">
          <p className="text-xs text-swiss-stone mb-1">{t('dataAlignment.patterns.weekdayAverage')}</p>
          <p className="text-lg font-medium">
            {unit === 'kWh'
              ? `${weekdayAverage.toFixed(0)} ${unit}`
              : `$${weekdayAverage.toFixed(2)}/MWh`}
          </p>
        </div>
        <div className="bg-swiss-pearl p-3 rounded-lg">
          <p className="text-xs text-swiss-stone mb-1">{t('dataAlignment.patterns.weekendAverage')}</p>
          <p className="text-lg font-medium">
            {unit === 'kWh'
              ? `${weekendAverage.toFixed(0)} ${unit}`
              : `$${weekendAverage.toFixed(2)}/MWh`}
          </p>
        </div>
      </div>

      {/* Seasonal Variation */}
      <div className="mb-6">
        <h5 className="text-sm font-medium text-swiss-charcoal mb-3">{t('dataAlignment.patterns.seasonalVariation')}</h5>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(seasonalAverages).map(([season, avg]) => (
            <div key={season} className="bg-swiss-pearl p-2 rounded text-center">
              <p className="text-xs text-swiss-stone capitalize">{t(`dataAlignment.patterns.seasons.${season}`)}</p>
              <p className="text-sm font-medium">
                {unit === 'kWh'
                  ? `${avg.toFixed(0)} ${unit}`
                  : `$${avg.toFixed(2)}/MWh`}
              </p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};